import React from 'react';
import { motion } from 'framer-motion';
import AnimatedList, { AnimatedListItem } from '@/components/lib/magicui/animated-list';

export default function AboutStory() {
  // Timeline items for the 20-year journey
  const timelineItems: AnimatedListItem[] = [
    {
      id: 'early-2000s',
      label: '2006 - 2020',
      title: 'Running brand agencies',
      body: 'Fourteen years owning the client relationship and directing a team of branding experts. Strategy, naming, identity, websites. This is where I learned what is specific to one business and cannot be copied to another.',
    },
    {
      id: '2000s-2010s',
      label: '2011 - 2016',
      title: 'Founded, built and sold Netstars',
      body: 'A two-sided marketplace helping musicians, writers and filmmakers make a living from digital work. I wrote the first version myself, then hired and led the engineers who took it forward while I ran product, design, marketing and operations. Sold in 2016.',
    },
    {
      id: '2010s',
      label: 'Dec 2014 - Jan 2018',
      title: 'Founded, built and sold RealVR',
      body: 'A marketplace for VR experiences, built by reusing the Netstars platform: games, 3D films, interactive experiences, models and assets, whole worlds, characters and real-world object scans. Sold in January 2018, after Facebook changed the Oculus store rules and removed the ability for users to buy inside it.',
    },
    {
      id: 'late-2010s-2020s',
      label: 'Along the way',
      title: 'Contract work for Microsoft and Google',
      body: 'Helped develop the interface for Windows Phone 7, then went out and promoted the operating system. The same kind of work for Google on Android, including demonstrating a new release live at Google Zeitgeist, their invite-only leadership conference.',
    },
    {
      id: 'today',
      label: 'Today',
      title: 'Attain Domains',
      body: 'I\'ve used AI every day since ChatGPT launched. Attain Domains is where I run it on a real business: listings, enquiries, follow-up and the admin behind a sale, handled by the same system I build for clients.',
    },
  ];

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const headingVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const headingItemVariants = {
    hidden: { opacity: 0, y: 10 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  const leftColumnVariants = {
    hidden: { opacity: 0, x: -24 },
    show: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        delay: 0.3,
      },
    },
  };

  const rightColumnVariants = {
    hidden: { opacity: 0, x: 24 },
    show: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        delay: 0.4,
      },
    },
  };

  return (
    <motion.section
      id="story"
      aria-label="Founder story and 20-year journey"
      variants={sectionVariants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      className="relative w-full py-20 md:py-24 bg-gradient-to-b from-white via-slate-50 to-white"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Top heading block */}
          <motion.div
            variants={headingVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="max-w-3xl mx-auto text-center mb-12 md:mb-16"
          >
            <motion.div variants={headingItemVariants}>
              <p className="text-xs md:text-sm font-semibold tracking-wide text-slate-500 uppercase mb-4">
                THE OPERATOR ADVANTAGE
              </p>
            </motion.div>

            <motion.div variants={headingItemVariants}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6">
                The 20-year journey behind Attain Domains.
              </h2>
            </motion.div>

            <motion.div variants={headingItemVariants}>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
                You're not hiring an agency layer. You're working directly with the builder who has spent two decades starting, running and selling online businesses, and who now builds the AI systems that run them.
              </p>
            </motion.div>
          </motion.div>

          {/* Two-column grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-start">
            {/* Left column: Story narrative */}
            <motion.div
              variants={leftColumnVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              className="space-y-6"
            >
              <div className="space-y-4">
                <h3 className="text-xl md:text-2xl font-semibold text-foreground">
                  The "operator" advantage
                </h3>
                <div className="space-y-4 text-slate-700 leading-relaxed">
                  <p>
                    I didn't start as a service provider. I started as a builder.
                  </p>
                  <p>
                    For the last 20 years I've been exactly where you are now, building online businesses for myself and not only for clients. Two of them were marketplaces I founded, built and sold.
                  </p>
                  <p>
                    I know the rush of locking in the perfect domain. I also know what it costs to run a business where every routine task still lands on your desk at the end of the day.
                  </p>
                </div>
              </div>

              <div className="space-y-4 pt-6">
                <h3 className="text-xl md:text-2xl font-semibold text-foreground">
                  Why I built Attain Domains
                </h3>
                <div className="space-y-4 text-slate-700 leading-relaxed">
                  <p>
                    To be transparent: I don't run this studio because I need the billable hours. I run it because it is the best proof I have. Attain Domains is a working business, and the AI system I build for clients is what runs it.
                  </p>
                  <p>
                    After years of building and selling my own companies I kept seeing the same pattern: founders would secure a premium domain, launch something, and then spend every week on admin the business should have been handling by itself.
                  </p>
                  <p>
                    I created Attain Domains to be the partner I wish I'd had 20 years ago: someone who treats your domain like equity, your website like an engine that has to earn its keep, and the business behind both like something that should run without you holding it up.
                  </p>
                </div>
              </div>

              <div className="pt-6">
                <p className="text-slate-700 leading-relaxed">
                  Today I work with expert service providers, boutique agencies and domain owners who want the name, the site and the system behind them working as one thing. If that's you, this is where the story starts to get interesting.
                </p>
              </div>
            </motion.div>

            {/* Right column: Animated timeline */}
            <motion.div
              variants={rightColumnVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              className="lg:sticky lg:top-24"
            >
              <AnimatedList items={timelineItems} delay={0.2} />
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

